import ActionPanel from '@/components/ActionPanel';
import PageContent from '@/components/PageContent';
import { AdvanceShareStyle, AdvanceShareStyleStatusEnum, AdvanceShareStyleStatusList } from '@/models/advance-share-style.model';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { updateAdvanceShareStyleStatus } from '@/services/advance-share-style.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { Icon } from '@rsuite/icons';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { MdPlayCircle } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { Button, Message, PanelGroup, SelectPicker, Stack, useToaster } from 'rsuite';



interface AdvanceShareStyleActionsProps{
  formObject?: AdvanceShareStyle;
  fetchData: () => void;    
};

const AdvanceShareStyleActions: React.FC<AdvanceShareStyleActionsProps> = ({formObject, fetchData}) => {
  const [loadingUpdateStatus, setLoadingUpdateStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<AdvanceShareStyleStatusEnum|null>(null);

  const nav = useNavigate();
  const toaster = useToaster();


  useEffect(() => {
    setSelectedStatus(formObject?.status ?? null);
  }, [formObject]);
  
  const handleClickUpdateStatus = () => {
    if(!formObject) return;

    const handleYesClick = async () => {
      if(!selectedStatus) {
        toastErrorMessage("Please select a status to update.");
        return;
      }

      setLoadingUpdateStatus(true);
      try {
        const result: BaseResponseModel<null> = await updateAdvanceShareStyleStatus(formObject.id, selectedStatus!);
        if (result.success) {
          toastSuccessMessage(`Style Version Successfully Updated.`);
          fetchData();
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in updating Style Version:${errorModel?.errorMessage}`);
      }
      finally {
        setLoadingUpdateStatus(false);
        toaster.clear(); 
      }
    };

    const handleNoClick = () => {
      toaster.clear(); 
    };

    toaster.push(
      <Message type="info" closable>
        <p style={{marginBottom:10}}>Are you sure you want to <strong>Update Style Status</strong>?</p>
        <div>
          <Button appearance="primary" onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };

  return (
      <PageContent>
          <PanelGroup>
              <ActionPanel
                title='Update Style Status' 
                description="This action updates the status of the style version."
                actionBar = {
                  <Stack width='50%' spacing={6} justify='flex-end'>
                    <SelectPicker
                      data={AdvanceShareStyleStatusList}
                      placeholder="Select a status"
                      value={selectedStatus}
                      onChange={val => setSelectedStatus(val)}
                    />
                    <Button 
                        startIcon={<Icon as={MdPlayCircle} />} 
                        appearance="ghost" 
                        onClick={handleClickUpdateStatus}
                        disabled={!formObject}
                        loading={loadingUpdateStatus}
                    >Update Status</Button>                  
                  </Stack>}
                onClick={handleClickUpdateStatus}
                disabled={!formObject}
                loading={loadingUpdateStatus}
              />
          </PanelGroup>
      </PageContent>
  );
};

export default AdvanceShareStyleActions;
